var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import SamplePlayer from './SamplePlayer';
// Using require() to import JS in TS files
var soundApi = require('./sound');
var constants = require('../constants');
// Default to 4/4 time
var BEATS_PER_MEASURE = 4;
var DEFAULT_BPM = 120;
/**
 * Main music player component which maintains the list of playback events and
 * uses a {@link SamplePlayer} to play sounds.
 */
var MusicPlayer = /** @class */ (function () {
    function MusicPlayer(bpm) {
        if (bpm === void 0) { bpm = DEFAULT_BPM; }
        this.bpm = bpm;
        this.playbackEvents = [];
        this.tracksMetadata = {};
        this.uniqueInvocationIdUpto = 0;
        this.samplePlayer = new SamplePlayer();
        this.library = null;
        this.lastTriggeredMeasure = 0;
        this.lastWhenRunMeasure = 0;
    }
    /**
     * Initializes the MusicPlayer and {@link SamplePlayer} with the music library.
     * Playback cannot start until the player is initialized.
     */
    MusicPlayer.prototype.initialize = function (library) {
        this.library = library;
        this.samplePlayer.initialize(library);
    };
    /**
     * Load a sound into the sound system using the given buffer. Currently only
     * used to upload custom sounds (hidden/demo-only feature).
     *
     * @param id
     * @param buffer
     */
    MusicPlayer.prototype.loadSoundFromBuffer = function (id, buffer) {
        soundApi.LoadSoundFromBuffer(id, buffer);
    };
    /**
     * Schedule a given sound (identified by its ID) for playback, at a given measure.
     * If playback is in progress, the sound will be played by the {@link SamplePlayer}
     *
     * @param id unique ID of the sound
     * @param measure when the sound should be played
     * @param insideWhenRun whether the sound was scheduled via normal playback, or triggered
     * @param trackId (optional) track ID this sound belongs to
     * @param functionContext (optional) function context this sound belongs to
     */
    MusicPlayer.prototype.playSoundAtMeasureById = function (id, measure, insideWhenRun, trackId, functionContext, skipContext, effects, blockId) {
        if (!this.samplePlayer.initialized() || this.library === null) {
            console.log('MusicPlayer not initialized');
            return;
        }
        var soundData = this.library.getSoundForId(id);
        if (!id || soundData === null || !measure) {
            console.log("Invalid input. id: ".concat(id, " measure: ").concat(measure));
            return;
        }
        var endingMeasure = measure + soundData.length - 1;
        if (insideWhenRun) {
            this.lastWhenRunMeasure = Math.max(endingMeasure, this.lastWhenRunMeasure);
        }
        else {
            this.lastTriggeredMeasure = Math.max(endingMeasure, this.lastTriggeredMeasure);
        }
        var soundEvent = {
            type: 'sound',
            id: id,
            triggered: !insideWhenRun,
            when: measure,
            trackId: trackId,
            functionContext: functionContext,
            skipContext: skipContext,
            effects: effects,
            length: soundData.length,
            soundType: soundData.type,
            blockId: blockId
        };
        this.playbackEvents.push(soundEvent);
        if (this.samplePlayer.playing()) {
            this.samplePlayer.playSamples(this.convertEventToSamples(soundEvent));
        }
    };
    MusicPlayer.prototype.playPatternAtMeasureById = function (value, measure, insideWhenRun, trackId, functionContext, skipContext, effects, blockId) {
        if (!this.samplePlayer.initialized()) {
            console.log('MusicPlayer not initialized');
            return;
        }
        if (!value || !measure) {
            console.log("Invalid input. pattern value: ".concat(value, " measure: ").concat(measure));
            return;
        }
        var patternEvent = {
            type: 'pattern',
            value: value,
            triggered: !insideWhenRun,
            when: measure,
            trackId: trackId,
            functionContext: functionContext,
            skipContext: skipContext,
            effects: effects,
            length: constants.DEFAULT_PATTERN_LENGTH,
            blockId: blockId
        };
        this.playbackEvents.push(patternEvent);
        if (this.samplePlayer.playing()) {
            this.samplePlayer.playSamples(this.convertEventToSamples(patternEvent));
        }
    };
    MusicPlayer.prototype.playChordAtMeasure = function (value, measure, insideWhenRun, trackId, functionContext, skipContext, effects, blockId) {
        if (!this.samplePlayer.initialized()) {
            console.log('MusicPlayer not initialized');
            return;
        }
        if (!value || !measure) {
            console.log("Invalid input. pattern value: ".concat(value, " measure: ").concat(measure));
            return;
        }
        var chordEvent = {
            type: 'chord',
            value: value,
            triggered: !insideWhenRun,
            when: measure,
            trackId: trackId,
            functionContext: functionContext,
            skipContext: skipContext,
            effects: effects,
            length: constants.DEFAULT_CHORD_LENGTH,
            blockId: blockId
        };
        this.playbackEvents.push(chordEvent);
        if (this.samplePlayer.playing()) {
            this.samplePlayer.playSamples(this.convertEventToSamples(chordEvent));
        }
    };
    /**
     * Preview the given sound. Plays immediately.
     *
     * @param id unique ID of the sound
     * @param onStop called when the sound finished playing
     */
    MusicPlayer.prototype.previewSound = function (id, onStop) {
        this.samplePlayer.previewSample(id, onStop);
    };
    MusicPlayer.prototype.previewChord = function (chordValue, onStop) {
        var chordEvent = {
            type: 'chord',
            when: 1,
            value: chordValue,
            triggered: false,
            length: constants.DEFAULT_CHORD_LENGTH
        };
        this.samplePlayer.previewSamples(this.convertEventToSamples(chordEvent), onStop);
    };
    /**
     * Cancels any ongoing previews.
     */
    MusicPlayer.prototype.cancelPreviews = function () {
        this.samplePlayer.cancelPreviews();
    };
    /**
     * Start playback. Schedules all queued playback events for playback
     * and tells the {@link SamplePlayer} to start playing.
     */
    MusicPlayer.prototype.playSong = function () {
        var sampleEvents = [];
        for (var _i = 0, _a = this.playbackEvents; _i < _a.length; _i++) {
            var event_1 = _a[_i];
            sampleEvents.push.apply(sampleEvents, this.convertEventToSamples(event_1));
        }
        this.samplePlayer.startPlayback(sampleEvents);
    };
    /**
     * Stop playback. Tells the {@link SamplePlayer} to stop all sample playback.
     */
    MusicPlayer.prototype.stopSong = function () {
        this.samplePlayer.stopPlayback();
        this.clearTriggeredEvents();
    };
    /**
     * Clear all non-triggered events from the list of playback events, and stop
     * any sounds that have not yet been played, if playback is in progress.
     */
    MusicPlayer.prototype.clearWhenRunEvents = function () {
        var _this = this;
        this.playbackEvents = this.playbackEvents.filter(function (playbackEvent) { return playbackEvent.triggered; });
        Object.keys(this.tracksMetadata).forEach(function (trackId) {
            if (_this.tracksMetadata[trackId].insideWhenRun) {
                delete _this.tracksMetadata[trackId];
            }
        });
        // If playing, stop all non-triggered samples that have not yet been played.
        this.samplePlayer.stopAllSamplesStillToPlay();
        this.lastWhenRunMeasure = 0;
    };
    MusicPlayer.prototype.getPlaybackEvents = function () {
        return this.playbackEvents;
    };
    MusicPlayer.prototype.getLastMeasure = function () {
        return Math.max(this.lastWhenRunMeasure, this.lastTriggeredMeasure);
    };
    // Returns the current playhead position, in floating point for an exact position,
    // 1-based, and scaled to measures.
    // Returns 0 if music is not playing.
    MusicPlayer.prototype.getCurrentPlayheadPosition = function () {
        var elapsedTime = this.samplePlayer.getElapsedPlaybackTimeSeconds();
        if (elapsedTime === -1) {
            return 0;
        }
        return this.convertSecondsToPlayheadPosition(elapsedTime);
    };
    // Converts actual seconds used by the audio system into a playhead
    // position, which is 1-based and scaled to measures.
    MusicPlayer.prototype.convertSecondsToPlayheadPosition = function (seconds) {
        return 1 + seconds / this.secondsPerMeasure();
    };
    // Converts a playhead position, which is 1-based and scaled to measures,
    // into actual seconds used by the audio system.
    MusicPlayer.prototype.convertPlayheadPositionToSeconds = function (playheadPosition) {
        return this.secondsPerMeasure() * (playheadPosition - 1);
    };
    MusicPlayer.prototype.secondsPerMeasure = function () {
        return (60 / this.bpm) * BEATS_PER_MEASURE;
    };
    /**
     * Create a new track.
     *
     * @param id unique ID of this track
     * @param name display name of the track (does not have to be unique)
     * @param measureStart starting measure of the track
     * @param insideWhenRun whether the track was scheduled via standard playback, or triggered
     */
    MusicPlayer.prototype.createTrack = function (id, name, measureStart, insideWhenRun) {
        if (id === null) {
            console.warn("Invalid track ID");
            return;
        }
        if (this.tracksMetadata[id]) {
            console.warn("Track ".concat(id, ": ").concat(name, " already exists!"));
            return;
        }
        this.tracksMetadata[id] = {
            name: name,
            insideWhenRun: insideWhenRun,
            currentMeasure: measureStart,
            maxConcurrentSounds: 0
        };
    };
    /**
     * Add the given sounds to the track identified by the track Id
     *
     * @param trackId
     * @param soundIds
     */
    MusicPlayer.prototype.addSoundsToTrack = function (trackId) {
        var _a;
        var soundIds = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            soundIds[_i - 1] = arguments[_i];
        }
        if (this.library === null) {
            console.warn('MusicPlayer not initialized');
            return;
        }
        if (!this.tracksMetadata[trackId]) {
            console.warn('No track with ID: ' + trackId);
            return;
        }
        var _b = this.tracksMetadata[trackId], currentMeasure = _b.currentMeasure, insideWhenRun = _b.insideWhenRun;
        var maxSoundLength = 0;
        for (var _c = 0, soundIds_1 = soundIds; _c < soundIds_1.length; _c++) {
            var soundId = soundIds_1[_c];
            this.playSoundAtMeasureById(soundId, currentMeasure, insideWhenRun, trackId);
            maxSoundLength = Math.max(maxSoundLength, ((_a = this.library.getSoundForId(soundId)) === null || _a === void 0 ? void 0 : _a.length) || 0);
        }
        this.tracksMetadata[trackId].currentMeasure += maxSoundLength;
        this.tracksMetadata[trackId].maxConcurrentSounds = Math.max(soundIds.length, this.tracksMetadata[trackId].maxConcurrentSounds);
    };
    /**
     * Add a rest of the given length to the track identified by the track ID.
     *
     * @param trackId
     * @param lengthMeasures
     */
    MusicPlayer.prototype.addRestToTrack = function (trackId, lengthMeasures) {
        if (!this.tracksMetadata[trackId]) {
            console.warn('No track with ID: ' + trackId);
            return;
        }
        this.tracksMetadata[trackId].currentMeasure += lengthMeasures;
    };
    MusicPlayer.prototype.clearTracksData = function () {
        this.tracksMetadata = {};
    };
    MusicPlayer.prototype.getTracksMetadata = function () {
        return this.tracksMetadata;
    };
    // Called by interpreted code in the simple2 model, this returns
    // a unique value that is used to differentiate each invocation of
    // a function, so that the timeline renderer can group relevant events.
    MusicPlayer.prototype.getUniqueInvocationId = function () {
        return this.uniqueInvocationIdUpto++;
    };
    // Returns an object containing two arrays.  One has the block IDs of all
    // blocks currently playing, while the other is for those not currently
    // playing.
    MusicPlayer.prototype.getCurrentlyPlayingBlockIds = function () {
        var currentPlayheadPosition = this.getCurrentPlayheadPosition();
        var playbackEvents = this.getPlaybackEvents();
        var playingBlockIds = [];
        playbackEvents.forEach(function (playbackEvent) {
            var currentlyPlaying = currentPlayheadPosition !== 0 &&
                currentPlayheadPosition >= playbackEvent.when &&
                currentPlayheadPosition < playbackEvent.when + playbackEvent.length;
            if (currentlyPlaying) {
                playingBlockIds.push(playbackEvent.blockId || '');
            }
        });
        return playingBlockIds;
    };
    /**
     * Clear all triggered events from the list of playback events.
     */
    MusicPlayer.prototype.clearTriggeredEvents = function () {
        var _this = this;
        this.playbackEvents = this.playbackEvents.filter(function (playbackEvent) { return !playbackEvent.triggered; });
        Object.keys(this.tracksMetadata).forEach(function (trackId) {
            if (!_this.tracksMetadata[trackId].insideWhenRun) {
                delete _this.tracksMetadata[trackId];
            }
        });
        this.lastTriggeredMeasure = 0;
    };
    MusicPlayer.prototype.convertEventToSamples = function (event) {
        var _a;
        if (this.library === null) {
            console.warn('Music Player not initialized');
            return [];
        }
        if ((_a = event.skipContext) === null || _a === void 0 ? void 0 : _a.skipSound) {
            return [];
        }
        if (event.type === 'sound') {
            var soundEvent = event;
            return [
                {
                    sampleId: soundEvent.id,
                    offsetSeconds: this.convertPlayheadPositionToSeconds(soundEvent.when),
                    triggered: soundEvent.triggered,
                    effects: soundEvent.effects
                }
            ];
        }
        else if (event.type === 'pattern') {
            var patternEvent = event;
            var results = [];
            var kit = patternEvent.value.kit;
            for (var _i = 0, _b = patternEvent.value.events; _i < _b.length; _i++) {
                var event_2 = _b[_i];
                var resultEvent = {
                    sampleId: "".concat(kit, "/").concat(event_2.src),
                    offsetSeconds: this.convertPlayheadPositionToSeconds(patternEvent.when + (event_2.tick - 1) / 16),
                    triggered: patternEvent.triggered,
                    effects: patternEvent.effects
                };
                results.push(resultEvent);
            }
            return results;
        }
        else if (event.type === 'chord') {
            var chordEvent = event;
            var _c = chordEvent.value, instrument = _c.instrument, notes = _c.notes, playStyle = _c.playStyle;
            if (notes.length === 0) {
                return [];
            }
            var results = [];
            var folder = this.library.getFolderForPath(instrument);
            if (folder === null) {
                console.warn("No instrument ".concat(instrument));
                return [];
            }
            if (playStyle === 'arpeggio-up') {
                notes.sort();
            }
            else if (playStyle === 'arpeggio-down') {
                notes.sort().reverse();
            }
            else if (playStyle === 'arpeggio-random') {
                // Randomize using Fisher-Yates Algorithm
                for (var i = notes.length - 1; i > 0; i--) {
                    var j = Math.floor(Math.random() * (i + 1));
                    var temp = notes[i];
                    notes[i] = notes[j];
                    notes[j] = temp;
                }
            }
            var _loop_1 = function (i) {
                var note = notes[i % notes.length];
                var sound = folder.sounds.find(function (sound) { return sound.note === note; }) || null;
                if (sound === null) {
                    console.warn("No sound for note value ".concat(note, " on instrument ").concat(instrument));
                    return "continue";
                }
                var noteWhen = playStyle === 'together' ? chordEvent.when : chordEvent.when + i / 16;
                results.push(__assign({ sampleId: "".concat(instrument, "/").concat(sound.src), offsetSeconds: this_1.convertPlayheadPositionToSeconds(noteWhen) }, event));
            };
            var this_1 = this;
            // Create the array of samples. If the play style is arpeggio, we play one
            // sound every 16th note, repeating the sequence as many times as needed.
            // If the play style is "together", then only add each note once, and have them
            // all play at the start of the given measure.
            for (var i = 0; i < (playStyle === 'together' ? notes.length : 16); i++) {
                _loop_1(i);
            }
            return results;
        }
        return [];
    };
    return MusicPlayer;
}());
export default MusicPlayer;
